// Calculated fields: a formula over a source's columns, worked out in the browser from the rows a
// widget fetches. The query API only takes plain field/aggregation pairs, so a calculation names
// the aggregates it needs (`sum(total_impressions) / sum(expected_impressions)`), the widget asks
// for those alongside its own metrics, and the formula is evaluated on what comes back.
//
// Two kinds, as in most BI tools:
//   measure   – a number per row of the result, recomputed after any regrouping, so a ratio of
//               two sums stays a ratio of the sums rather than a sum of ratios.
//   dimension – a label per row at a chosen grain (`if([Delivery %] > 1, 'Over', 'Under')`
//               per campaign), which the widget can then group, colour or filter by.

export const DASHBOARD_CALC_KINDS = ['measure', 'dimension'] as const;
export type DashboardCalcKind = (typeof DASHBOARD_CALC_KINDS)[number];

export const DASHBOARD_CALC_FORMATS = ['number', 'percent'] as const;
export type DashboardCalcFormat = (typeof DASHBOARD_CALC_FORMATS)[number];

export type DashboardCalculatedField = {
    id: string;
    name: string;
    kind: DashboardCalcKind;
    expression: string;
    // Measures only. 'percent' reads the value as a ratio: 1.0984 shows as 109.84%.
    format?: DashboardCalcFormat;
    // Dimensions only: the fields it is worked out per before the widget groups the rows, e.g.
    // ['campaign_id'] to label each campaign. Empty means "per the widget's own dimensions".
    grain?: string[];
    // The source it is written against, in the global-filter vocabulary ('default' or a
    // datasource id). Sources don't share column names, so a calculation belongs to one.
    source?: string;
};

// How a calculated field is referenced from a widget's metrics, dimensions and filters.
export const CALC_PREFIX = 'calc__';
export const calcFieldOf = (id: string): string => `${CALC_PREFIX}${id}`;
export const isCalcField = (field: string | undefined): boolean =>
    typeof field === 'string' && field.startsWith(CALC_PREFIX);
export const calcIdOf = (field: string): string => field.slice(CALC_PREFIX.length);

// A measure sits among a widget's metrics, which are keyed `<field>_<aggregation>`. The
// aggregation means nothing for a calculation; 'sum' just gives it the same key shape.
export const CALC_METRIC_AGGREGATION = 'sum' as const;
export const calcMetricKey = (id: string): string =>
    `${calcFieldOf(id)}_${CALC_METRIC_AGGREGATION}`;

// How many fetched rows went into a row of the result: `rows()` in a formula.
export const ROW_COUNT_KEY = '__rows';

const AGGREGATE_FUNCTIONS = ['sum', 'avg', 'min', 'max', 'count'] as const;
type AggregateFunction = (typeof AGGREGATE_FUNCTIONS)[number];
const isAggregateFunction = (name: string): name is AggregateFunction =>
    (AGGREGATE_FUNCTIONS as readonly string[]).includes(name);

const SCALAR_FUNCTIONS = [
    'if',
    'abs',
    'round',
    'floor',
    'ceil',
    'coalesce',
    'contains',
    'concat',
    'lower',
    'upper',
] as const;
type ScalarFunction = (typeof SCALAR_FUNCTIONS)[number];
const isScalarFunction = (name: string): name is ScalarFunction =>
    (SCALAR_FUNCTIONS as readonly string[]).includes(name);

// Shown in the editor as the vocabulary a formula can use.
export const DASHBOARD_CALC_FUNCTIONS = ['rows', ...AGGREGATE_FUNCTIONS, ...SCALAR_FUNCTIONS];

// ---- Parsing ----

type Token =
    | { type: 'num'; value: number }
    | { type: 'str'; value: string }
    | { type: 'ident'; value: string }
    | { type: 'ref'; value: string }
    | { type: 'op'; value: string };

export type CalcNode =
    | { type: 'num'; value: number }
    | { type: 'str'; value: string }
    | { type: 'field'; name: string }
    | { type: 'ref'; name: string }
    | { type: 'agg'; fn: AggregateFunction; field: string }
    | { type: 'rows' }
    | { type: 'call'; fn: ScalarFunction; args: CalcNode[] }
    | { type: 'unary'; op: '-' | 'not'; arg: CalcNode }
    | { type: 'binary'; op: string; left: CalcNode; right: CalcNode };

// Errors carry a key under Pages.DashboardHome.custom.calcErrors and what to fill it in with,
// so the editor can show them translated.
export class CalcError extends Error {
    constructor(public key: string, public params: Record<string, string | number> = {}) {
        super(key);
    }
}

const OPERATORS = [
    '>=',
    '<=',
    '!=',
    '<>',
    '==',
    '+',
    '-',
    '*',
    '/',
    '%',
    '(',
    ')',
    ',',
    '>',
    '<',
    '=',
];

const tokenize = (input: string): Token[] => {
    const tokens: Token[] = [];
    let i = 0;
    while (i < input.length) {
        const c = input[i];
        if (/\s/.test(c)) {
            i += 1;
        } else if (/[0-9.]/.test(c)) {
            const match = /^(\d+\.?\d*|\.\d+)(e[+-]?\d+)?/i.exec(input.slice(i));
            if (!match) throw new CalcError('unexpected', { text: c, position: i + 1 });
            tokens.push({ type: 'num', value: Number(match[0]) });
            i += match[0].length;
        } else if (c === "'" || c === '"') {
            const end = input.indexOf(c, i + 1);
            if (end < 0) throw new CalcError('unclosedString', { position: i + 1 });
            tokens.push({ type: 'str', value: input.slice(i + 1, end) });
            i = end + 1;
        } else if (c === '[') {
            const end = input.indexOf(']', i + 1);
            if (end < 0) throw new CalcError('unclosedRef', { position: i + 1 });
            tokens.push({ type: 'ref', value: input.slice(i + 1, end).trim() });
            i = end + 1;
        } else if (/[A-Za-z_]/.test(c)) {
            const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(input.slice(i)) as RegExpExecArray;
            tokens.push({ type: 'ident', value: match[0] });
            i += match[0].length;
        } else {
            const op = OPERATORS.find(o => input.startsWith(o, i));
            if (!op) throw new CalcError('unexpected', { text: c, position: i + 1 });
            tokens.push({ type: 'op', value: op });
            i += op.length;
        }
    }
    return tokens;
};

export const parseCalcExpression = (input: string): CalcNode => {
    if (!input.trim()) throw new CalcError('empty');
    const tokens = tokenize(input);
    let pos = 0;
    const peek = (): Token | undefined => tokens[pos];
    const isOp = (value: string) => {
        const tk = peek();
        return tk?.type === 'op' && tk.value === value;
    };
    const isWord = (value: string) => {
        const tk = peek();
        return tk?.type === 'ident' && tk.value.toLowerCase() === value;
    };
    const expectOp = (value: string) => {
        if (!isOp(value)) throw new CalcError('expected', { text: value });
        pos += 1;
    };

    // Each level hands down to the next tighter-binding one.
    let parseOr: () => CalcNode;

    const parsePrimary = (): CalcNode => {
        const tk = peek();
        if (!tk) throw new CalcError('unexpectedEnd');
        pos += 1;
        if (tk.type === 'num') return { type: 'num', value: tk.value };
        if (tk.type === 'str') return { type: 'str', value: tk.value };
        if (tk.type === 'ref') return { type: 'ref', name: tk.value };
        if (tk.type === 'op') {
            if (tk.value !== '(') throw new CalcError('unexpected', { text: tk.value });
            const inner = parseOr();
            expectOp(')');
            return inner;
        }
        // An identifier: a function call when followed by '(', otherwise a column.
        if (!isOp('(')) return { type: 'field', name: tk.value };
        pos += 1;
        const fn = tk.value.toLowerCase();
        const args: CalcNode[] = [];
        if (!isOp(')')) {
            args.push(parseOr());
            while (isOp(',')) {
                pos += 1;
                args.push(parseOr());
            }
        }
        expectOp(')');
        if (fn === 'rows') {
            if (args.length) throw new CalcError('arity', { fn, count: 0 });
            return { type: 'rows' };
        }
        if (isAggregateFunction(fn)) {
            const [arg] = args;
            if (args.length !== 1 || arg.type !== 'field') {
                throw new CalcError('aggregateNeedsField', { fn });
            }
            return { type: 'agg', fn, field: arg.name };
        }
        if (!isScalarFunction(fn)) throw new CalcError('unknownFunction', { fn: tk.value });
        const arity: Partial<Record<ScalarFunction, [number, number]>> = {
            if: [3, 3],
            abs: [1, 1],
            round: [1, 2],
            floor: [1, 1],
            ceil: [1, 1],
            contains: [2, 2],
            lower: [1, 1],
            upper: [1, 1],
        };
        const [min, max] = arity[fn] ?? [1, Infinity];
        if (args.length < min || args.length > max) {
            throw new CalcError('arity', { fn, count: min });
        }
        return { type: 'call', fn, args };
    };

    const parseUnary = (): CalcNode => {
        if (isOp('-')) {
            pos += 1;
            return { type: 'unary', op: '-', arg: parseUnary() };
        }
        if (isOp('+')) {
            pos += 1;
            return parseUnary();
        }
        return parsePrimary();
    };

    const binaryLevel =
        (next: () => CalcNode, ops: string[]): (() => CalcNode) =>
        () => {
            let left = next();
            for (;;) {
                const tk = peek();
                const op =
                    tk && (tk.type === 'op' || tk.type === 'ident') ? tk.value.toLowerCase() : '';
                if (!ops.includes(op)) return left;
                pos += 1;
                left = { type: 'binary', op, left, right: next() };
            }
        };

    const parseMul = binaryLevel(parseUnary, ['*', '/', '%']);
    const parseAdd = binaryLevel(parseMul, ['+', '-']);
    const parseCmp = (): CalcNode => {
        const left = parseAdd();
        const tk = peek();
        if (tk?.type === 'op' && ['>', '<', '>=', '<=', '=', '==', '!=', '<>'].includes(tk.value)) {
            pos += 1;
            return { type: 'binary', op: tk.value, left, right: parseAdd() };
        }
        return left;
    };
    const parseNot = (): CalcNode => {
        if (isWord('not')) {
            pos += 1;
            return { type: 'unary', op: 'not', arg: parseNot() };
        }
        return parseCmp();
    };
    const parseAnd = binaryLevel(parseNot, ['and']);
    parseOr = binaryLevel(parseAnd, ['or']);

    const tree = parseOr();
    const rest = peek();
    if (rest) throw new CalcError('unexpected', { text: String(rest.value) });
    return tree;
};

// ---- Analysis: what a calculation needs from the query ----

export type CalcMetricNeed = { field: string; aggregation: AggregateFunction };

export type CalcAnalysis = {
    // Aggregates to fetch, from this calculation and any it references.
    metrics: CalcMetricNeed[];
    // Columns read as-is (a dimension's own value), which the query has to group by.
    fields: string[];
    // Every calculation this one depends on, itself last.
    calcIds: string[];
};

const findCalc = (calcs: DashboardCalculatedField[], name: string) => {
    const lower = name.trim().toLowerCase();
    return calcs.find(c => c.name.trim().toLowerCase() === lower);
};

const parsedCache = new Map<string, CalcNode>();
const parsed = (expression: string): CalcNode => {
    const hit = parsedCache.get(expression);
    if (hit) return hit;
    const tree = parseCalcExpression(expression);
    parsedCache.set(expression, tree);
    return tree;
};

// Walk a calculation and the ones it references, collecting what has to be fetched. Throws a
// CalcError for anything that can't work: an unknown reference, a cycle, a column the source
// doesn't have, or a measure reading a bare column (it has no single value once rows are
// grouped, so it has to be wrapped in an aggregate).
export const analyzeCalculation = (
    calc: DashboardCalculatedField,
    calcs: DashboardCalculatedField[],
    knownFields?: string[],
): CalcAnalysis => {
    const metrics = new Map<string, CalcMetricNeed>();
    const fields = new Set<string>();
    const order: string[] = [];

    const visit = (current: DashboardCalculatedField, stack: string[]) => {
        if (stack.includes(current.id)) throw new CalcError('cycle', { name: current.name });
        if (order.includes(current.id)) return;
        const walk = (node: CalcNode) => {
            switch (node.type) {
                case 'field':
                    if (current.kind === 'measure') {
                        throw new CalcError('measureNeedsAggregate', { field: node.name });
                    }
                    if (knownFields && !knownFields.includes(node.name)) {
                        throw new CalcError('unknownField', { field: node.name });
                    }
                    fields.add(node.name);
                    return;
                case 'agg':
                    if (knownFields && !knownFields.includes(node.field)) {
                        throw new CalcError('unknownField', { field: node.field });
                    }
                    metrics.set(`${node.field}_${node.fn}`, {
                        field: node.field,
                        aggregation: node.fn,
                    });
                    return;
                case 'ref': {
                    const target = findCalc(calcs, node.name);
                    if (!target) throw new CalcError('unknownRef', { name: node.name });
                    if (current.kind === 'measure' && target.kind === 'dimension') {
                        throw new CalcError('measureUsesDimension', { name: target.name });
                    }
                    visit(target, [...stack, current.id]);
                    return;
                }
                case 'call':
                    node.args.forEach(walk);
                    return;
                case 'unary':
                    walk(node.arg);
                    return;
                case 'binary':
                    walk(node.left);
                    walk(node.right);
                    break;
                default:
                    break;
            }
        };
        walk(parsed(current.expression));
        (current.grain ?? []).forEach(f => fields.add(f));
        order.push(current.id);
    };
    visit(calc, []);
    return { metrics: [...metrics.values()], fields: [...fields], calcIds: order };
};

// The error a calculation would raise, or null when it is usable. For the editor.
export const calculationError = (
    calc: DashboardCalculatedField,
    calcs: DashboardCalculatedField[],
    knownFields?: string[],
): CalcError | null => {
    try {
        analyzeCalculation(calc, calcs, knownFields);
        return null;
    } catch (e) {
        return e instanceof CalcError ? e : new CalcError('invalid');
    }
};

// ---- Evaluation ----

type CalcValue = number | string | boolean | null;

const toNumber = (v: CalcValue): number | null => {
    if (v == null || v === '') return null;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const truthy = (v: CalcValue): boolean => {
    if (typeof v === 'string') return v !== '';
    return Boolean(v);
};

// A dimension's value as the response carries it: its display label where it has one.
export const rowDimensionValue = (row: Record<string, unknown>, field: string): unknown =>
    row[`${field}_`] ?? row[field];

export const evaluateCalc = (
    node: CalcNode,
    row: Record<string, unknown>,
    resolveRef: (name: string) => CalcValue,
): CalcValue => {
    const ev = (n: CalcNode) => evaluateCalc(n, row, resolveRef);
    switch (node.type) {
        case 'num':
        case 'str':
            return node.value;
        case 'field': {
            const v = rowDimensionValue(row, node.name);
            return v == null ? null : (v as CalcValue);
        }
        case 'agg':
            return toNumber(row[`${node.field}_${node.fn}`] as CalcValue);
        case 'rows':
            return toNumber((row[ROW_COUNT_KEY] as CalcValue) ?? 1);
        case 'ref':
            return resolveRef(node.name);
        case 'unary': {
            const v = ev(node.arg);
            if (node.op === 'not') return !truthy(v);
            const n = toNumber(v);
            return n == null ? null : -n;
        }
        case 'binary': {
            if (node.op === 'and') return truthy(ev(node.left)) && truthy(ev(node.right));
            if (node.op === 'or') return truthy(ev(node.left)) || truthy(ev(node.right));
            const l = ev(node.left);
            const r = ev(node.right);
            if (['=', '==', '!=', '<>'].includes(node.op)) {
                const same =
                    toNumber(l) != null && toNumber(r) != null
                        ? toNumber(l) === toNumber(r)
                        : String(l ?? '').toLowerCase() === String(r ?? '').toLowerCase();
                return node.op === '=' || node.op === '==' ? same : !same;
            }
            const a = toNumber(l);
            const b = toNumber(r);
            if (node.op === '+' && (typeof l === 'string' || typeof r === 'string')) {
                if (a == null || b == null) return `${l ?? ''}${r ?? ''}`;
            }
            if (a == null || b == null)
                return ['>', '<', '>=', '<='].includes(node.op) ? false : null;
            switch (node.op) {
                case '+':
                    return a + b;
                case '-':
                    return a - b;
                case '*':
                    return a * b;
                // Nothing to divide by is "no value", not infinity: it shows as a dash and
                // compares false, which is what a delivery % against no target should do.
                case '/':
                    return b === 0 ? null : a / b;
                case '%':
                    return b === 0 ? null : a % b;
                case '>':
                    return a > b;
                case '<':
                    return a < b;
                case '>=':
                    return a >= b;
                case '<=':
                    return a <= b;
                default:
                    return null;
            }
        }
        case 'call': {
            const args = node.args;
            switch (node.fn) {
                case 'if':
                    return truthy(ev(args[0])) ? ev(args[1]) : ev(args[2]);
                case 'coalesce':
                    return args.map(ev).find(v => v != null && v !== '') ?? null;
                case 'concat':
                    return args.map(a => String(ev(a) ?? '')).join('');
                case 'contains':
                    return String(ev(args[0]) ?? '')
                        .toLowerCase()
                        .includes(String(ev(args[1]) ?? '').toLowerCase());
                case 'lower':
                    return String(ev(args[0]) ?? '').toLowerCase();
                case 'upper':
                    return String(ev(args[0]) ?? '').toUpperCase();
                default: {
                    const n = toNumber(ev(args[0]));
                    if (n == null) return null;
                    if (node.fn === 'abs') return Math.abs(n);
                    if (node.fn === 'floor') return Math.floor(n);
                    if (node.fn === 'ceil') return Math.ceil(n);
                    const places = args[1] ? toNumber(ev(args[1])) ?? 0 : 0;
                    const scale = 10 ** places;
                    return Math.round(n * scale) / scale;
                }
            }
        }
        default:
            return null;
    }
};

// Work out every given calculation on a row, in dependency order, writing measures under their
// metric key and dimensions under their field (plus the `<field>_` label the charts read).
export const applyCalculationsToRow = (
    row: Record<string, unknown>,
    ids: string[],
    calcs: DashboardCalculatedField[],
): Record<string, unknown> => {
    const out: Record<string, unknown> = { ...row };
    const values = new Map<string, CalcValue>();
    const valueOf = (calc: DashboardCalculatedField): CalcValue => {
        if (values.has(calc.id)) return values.get(calc.id) ?? null;
        values.set(calc.id, null);
        let v: CalcValue = null;
        try {
            v = evaluateCalc(parsed(calc.expression), out, name => {
                const target = findCalc(calcs, name);
                return target ? valueOf(target) : null;
            });
        } catch {
            v = null;
        }
        if (typeof v === 'boolean') v = calc.kind === 'measure' ? Number(v) : String(v);
        values.set(calc.id, v);
        return v;
    };
    ids.forEach(id => {
        const calc = calcs.find(c => c.id === id);
        if (!calc) return;
        const v = valueOf(calc);
        if (calc.kind === 'measure') {
            out[calcMetricKey(calc.id)] = v;
        } else {
            const label = v == null || v === '' ? '—' : String(v);
            out[calcFieldOf(calc.id)] = label;
            out[`${calcFieldOf(calc.id)}_`] = label;
        }
    });
    return out;
};

// ---- Planning a widget's query, and shaping what comes back ----

type Metric = { field: string; aggregation: string };
type Dimension = { field: string; granularity?: string };
type Filter = {
    field: string;
    operator: string;
    value: string | number | string[];
    rolling?: string;
};

export type CalcWidgetLike<M extends Metric, D extends Dimension, F extends Filter> = {
    metrics: M[];
    dimensions: D[];
    filters: F[];
    sort: { field: string; direction: 'asc' | 'desc' }[];
};

export type WidgetQueryPlan<M extends Metric, D extends Dimension, F extends Filter> = {
    // What is sent: the widget's own metrics and dimensions without the calculated ones, plus
    // whatever those need fetched.
    metrics: M[];
    dimensions: D[];
    filters: F[];
    sort: { field: string; direction: 'asc' | 'desc' }[];
    // Worked out in the browser, in dependency order.
    calcIds: string[];
    clientFilters: F[];
    // The fetched rows are finer than the widget shows, so they're grouped back up.
    regroup: boolean;
    // Dimension calculations referenced that don't exist (deleted, or another source's).
    missing: string[];
};

export const planWidgetQuery = <M extends Metric, D extends Dimension, F extends Filter>(
    widget: CalcWidgetLike<M, D, F>,
    calcs: DashboardCalculatedField[],
): WidgetQueryPlan<M, D, F> => {
    const used = new Set<string>();
    [...widget.metrics, ...widget.dimensions, ...widget.filters].forEach(x => {
        if (isCalcField(x.field)) used.add(calcIdOf(x.field));
    });
    const metrics: M[] = widget.metrics.filter(m => !isCalcField(m.field));
    const shownDims: D[] = widget.dimensions.filter(d => !isCalcField(d.field));
    const dimensions: D[] = [...shownDims];
    const order: string[] = [];
    const missing: string[] = [];
    used.forEach(id => {
        const calc = calcs.find(c => c.id === id);
        if (!calc) {
            missing.push(id);
            return;
        }
        let analysis: CalcAnalysis;
        try {
            analysis = analyzeCalculation(calc, calcs);
        } catch {
            missing.push(id);
            return;
        }
        analysis.metrics.forEach(need => {
            if (!metrics.some(m => m.field === need.field && m.aggregation === need.aggregation)) {
                metrics.push(need as unknown as M);
            }
        });
        analysis.fields.forEach(field => {
            if (!dimensions.some(d => d.field === field)) dimensions.push({ field } as D);
        });
        analysis.calcIds.forEach(cid => {
            if (!order.includes(cid)) order.push(cid);
        });
    });
    // The API reads "no metrics" as a plain projection with no GROUP BY: one row per source row
    // rather than per dimension value. A widget whose only measure is rows() would be counting
    // those, so ask for a count to make the query group.
    if (metrics.length === 0 && dimensions.length > 0) {
        metrics.push({ field: dimensions[0].field, aggregation: 'count' } as unknown as M);
    }
    const clientFilters = widget.filters.filter(f => isCalcField(f.field));
    const regroup =
        dimensions.length > shownDims.length || widget.dimensions.some(d => isCalcField(d.field));
    const metricKeyOf = (field: string) => {
        const m = widget.metrics.find(x => x.field === field);
        return m ? `${m.field}_${m.aggregation}` : field;
    };
    return {
        metrics,
        dimensions,
        filters: widget.filters.filter(f => !isCalcField(f.field)),
        // Calculated columns don't exist on the server; the widget orders those itself.
        sort: widget.sort
            .filter(s => !isCalcField(s.field))
            .map(s => ({ field: metricKeyOf(s.field), direction: s.direction })),
        calcIds: order,
        clientFilters,
        regroup,
        missing,
    };
};

const matchesFilter = (value: unknown, filter: Filter): boolean => {
    const { operator } = filter;
    const target = filter.value;
    const asNum = (v: unknown) => (v === '' || v == null ? NaN : Number(v));
    const list = (Array.isArray(target) ? target : String(target).split(',')).map(v =>
        String(v).trim().toLowerCase(),
    );
    const text = String(value ?? '').toLowerCase();
    const like = (pattern: string) =>
        new RegExp(
            `^${pattern
                .toLowerCase()
                .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                .replace(/%/g, '.*')
                .replace(/_/g, '.')}$`,
        ).test(text);
    const numeric = !Number.isNaN(asNum(value)) && !Number.isNaN(asNum(target));
    switch (operator) {
        case 'eq':
            return numeric ? asNum(value) === asNum(target) : text === String(target).toLowerCase();
        case 'neq':
            return numeric ? asNum(value) !== asNum(target) : text !== String(target).toLowerCase();
        case 'gt':
            return numeric && asNum(value) > asNum(target);
        case 'gte':
            return numeric && asNum(value) >= asNum(target);
        case 'lt':
            return numeric && asNum(value) < asNum(target);
        case 'lte':
            return numeric && asNum(value) <= asNum(target);
        case 'like':
            return like(String(target));
        case 'not_like':
            return !like(String(target));
        case 'in':
            return list.includes(text);
        case 'not_in':
            return !list.includes(text);
        default:
            return true;
    }
};

const combine = (aggregation: string, a: unknown, b: unknown, rowsA: number, rowsB: number) => {
    const x = Number(a ?? 0);
    const y = Number(b ?? 0);
    if (aggregation === 'min') return Math.min(x, y);
    if (aggregation === 'max') return Math.max(x, y);
    // An average of averages, weighted by how many fetched rows each stood for.
    if (aggregation === 'avg')
        return rowsA + rowsB > 0 ? (x * rowsA + y * rowsB) / (rowsA + rowsB) : 0;
    return x + y;
};

// Fetched rows → the rows the widget shows: calculations worked out, calculated filters
// applied, and, where the query had to go finer than the widget's own dimensions, grouped back
// up with the measures recomputed on the totals.
export const shapeWidgetRows = <M extends Metric, D extends Dimension, F extends Filter>(
    rows: Record<string, unknown>[],
    widget: CalcWidgetLike<M, D, F>,
    plan: WidgetQueryPlan<M, D, F>,
    calcs: DashboardCalculatedField[],
): Record<string, unknown>[] => {
    if (plan.calcIds.length === 0 && !plan.regroup && plan.clientFilters.length === 0) {
        return rows;
    }
    const valueFor = (row: Record<string, unknown>, field: string) =>
        isCalcField(field) ? row[field] : rowDimensionValue(row, field);
    const calculated = rows
        .map(row => applyCalculationsToRow({ [ROW_COUNT_KEY]: 1, ...row }, plan.calcIds, calcs))
        .filter(row =>
            plan.clientFilters.every(f => {
                const id = calcIdOf(f.field);
                const calc = calcs.find(c => c.id === id);
                const value =
                    calc?.kind === 'measure' ? row[calcMetricKey(id)] : valueFor(row, f.field);
                return matchesFilter(value, f);
            }),
        );
    if (!plan.regroup) return calculated;

    const groups = new Map<string, Record<string, unknown>>();
    calculated.forEach(row => {
        const key = JSON.stringify(widget.dimensions.map(d => valueFor(row, d.field) ?? ''));
        const current = groups.get(key);
        if (!current) {
            groups.set(key, { ...row });
            return;
        }
        const rowsA = Number(current[ROW_COUNT_KEY] ?? 1);
        const rowsB = Number(row[ROW_COUNT_KEY] ?? 1);
        plan.metrics.forEach(m => {
            const k = `${m.field}_${m.aggregation}`;
            current[k] = combine(m.aggregation, current[k], row[k], rowsA, rowsB);
        });
        current[ROW_COUNT_KEY] = rowsA + rowsB;
    });
    // Measures are ratios and the like: work them out again on the grouped figures.
    const measureIds = plan.calcIds.filter(id => calcs.find(c => c.id === id)?.kind === 'measure');
    return [...groups.values()].map(row => applyCalculationsToRow(row, measureIds, calcs));
};

// One row adding up all of them: what a table's totals row and a single-value widget show.
// Measures are recomputed on the totals, so a delivery % is total delivered over total expected.
export const totalOfRows = (
    rows: Record<string, unknown>[],
    metrics: Metric[],
    calcs: DashboardCalculatedField[],
    calcIds: string[],
): Record<string, unknown> => {
    const total: Record<string, unknown> = { [ROW_COUNT_KEY]: 0 };
    rows.forEach((row, index) => {
        const rowsA = Number(total[ROW_COUNT_KEY]);
        const rowsB = Number(row[ROW_COUNT_KEY] ?? 1);
        metrics.forEach(m => {
            const k = `${m.field}_${m.aggregation}`;
            total[k] =
                index === 0
                    ? Number(row[k] ?? 0)
                    : combine(m.aggregation, total[k], row[k], rowsA, rowsB);
        });
        total[ROW_COUNT_KEY] = rowsA + rowsB;
    });
    const measureIds = calcIds.filter(id => calcs.find(c => c.id === id)?.kind === 'measure');
    return applyCalculationsToRow(total, measureIds, calcs);
};
